package org.example.project.screens_shared

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.MeineFahrzeugeApi
import org.example.project.utils.All

private const val CHECKED = "\u2705"
private const val UNCHECKED = "\u2B1C"

@Composable
fun SHAREDMeineZugmaschienen(art: String = org.example.project.service.currentSaveArt()) {
    MeineFahrzeugeScreen(
        art = art,
        type = MeineFahrzeugeApi.TYP_ZUGMASCHIENE,
        screenKey = "MeineZugmaschienen",
        title = "Meine Zugmaschienen",
        headers = listOf("ID", "Kennzeichen", "Fahrzeughersteller", "Name", "Aktiv"),
        headerDisplayLabels = listOf("ID", "Kennzeichen", "Fahrzeughersteller", "Name", "Aktiv"),
        toggleColumns = setOf("Aktiv")
    )
}

@Composable
fun SHAREDMeineAuflieger(art: String = org.example.project.service.currentSaveArt()) {
    MeineFahrzeugeScreen(
        art = art,
        type = MeineFahrzeugeApi.TYP_AUFLIEGER,
        screenKey = "MeineAuflieger",
        title = "Meine Auflieger",
        headers = listOf("ID", "Kennzeichen", "Fahrzeughersteller", "Name", "Aktiv", "Koffer", "Planer", "Kühlung"),
        headerDisplayLabels = listOf("ID", "Kennzeichen", "Fahrzeughersteller", "Name", "Aktiv", "Koffer", "Planer", "Kühlung"),
        toggleColumns = setOf("Aktiv", "Koffer", "Planer", "Kühlung")
    )
}

@Composable
private fun MeineFahrzeugeScreen(
    art: String,
    type: String,
    screenKey: String,
    title: String,
    headers: List<String>,
    headerDisplayLabels: List<String>,
    toggleColumns: Set<String> = emptySet()
) {
    var rows by remember(type) { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember(type) { mutableStateOf<Map<String, String>?>(null) }
    val scope = rememberCoroutineScope()
    val firmenId = remember(art) { MeineFahrzeugeApi.currentFirmenId() }

    suspend fun reload() {
        rows = letzteFilter?.let { MeineFahrzeugeApi.search(type, it) }
            .orEmpty().sortedBy { it.id.toIntOrNull() ?: 0 }
    }

    LaunchedEffect(type, art, firmenId) {
        reload()
    }

    TabularScreen(
        screenKey = screenKey,
        rollenKey = screenKey,
        title = title,
        headers = headers,
        headerDisplayLabels = headerDisplayLabels,
        models = rows,
        data = rows.map { entry ->
            entry.id to rowFor(type, entry)
        },
        editable = true,
        includeMetadata = true,
        nonEditableCols = setOf("ID"),
        toggleColumns = toggleColumns,
        createRow = { newId ->
            if (type == MeineFahrzeugeApi.TYP_AUFLIEGER) {
                listOf(newId, "", "", "", CHECKED, UNCHECKED, UNCHECKED, UNCHECKED)
            } else {
                listOf(newId, "", "", "", CHECKED)
            }
        },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                rows = MeineFahrzeugeApi.search(type, filters)
                    .sortedBy { it.id.toIntOrNull() ?: 0 }
            }
        },
        onCommit = { committedRows ->
            scope.launch {
                committedRows.forEach { (_, cols) ->
                    val entry = allFromRow(type, cols)
                    val existing = rows.firstOrNull { it.id == entry.id }
                    if (existing == null || existing != entry) {
                        MeineFahrzeugeApi.save(type, entry, firmenId)
                    }
                }
                reload()
            }
        },
        onDeleteRows = { ids ->
            scope.launch {
                ids.forEach { id -> MeineFahrzeugeApi.delete(type, id, firmenId) }
                reload()
            }
        }
    )
}

private fun rowFor(type: String, entry: All): List<String> =
    if (type == MeineFahrzeugeApi.TYP_AUFLIEGER) {
        listOf(
            entry.id,
            entry.kennzeichen,
            entry.fahrzeughersteller,
            entry.name,
            if (entry.aktiv) CHECKED else UNCHECKED,
            if (entry.koffer) CHECKED else UNCHECKED,
            if (entry.planer) CHECKED else UNCHECKED,
            if (entry.kuehlung) CHECKED else UNCHECKED
        )
    } else {
        listOf(
            entry.id,
            entry.kennzeichen,
            entry.fahrzeughersteller,
            entry.name,
            if (entry.aktiv) CHECKED else UNCHECKED
        )
    }

private fun allFromRow(type: String, cols: List<String>): All {
    val id = cols.getOrNull(0).orEmpty().trim()
    val kennzeichen = cols.getOrNull(1).orEmpty().trim()
    val fahrzeughersteller = cols.getOrNull(2).orEmpty().trim()
    val name = cols.getOrNull(3).orEmpty().trim()

    return if (type == MeineFahrzeugeApi.TYP_AUFLIEGER) {
        All(
            id = id,
            kennzeichen = kennzeichen,
            fahrzeughersteller = fahrzeughersteller,
            name = name,
            aktiv = isChecked(cols.getOrNull(4)),
            koffer = isChecked(cols.getOrNull(5)),
            planer = isChecked(cols.getOrNull(6)),
            kuehlung = isChecked(cols.getOrNull(7))
        )
    } else {
        All(
            id = id,
            kennzeichen = kennzeichen,
            fahrzeughersteller = fahrzeughersteller,
            name = name,
            aktiv = isChecked(cols.getOrNull(4))
        )
    }
}

private fun isChecked(value: String?): Boolean =
    value == CHECKED || value.equals("true", ignoreCase = true)
